import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast } from 'lucide-react';
import { useSettingsStore } from '../../stores/settingsStore';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface Column<T> {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (item: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor?: (item: T) => string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loading?: boolean;
  pageSize?: number;
  page?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  keyExtractor = (item: T) => (item.id as string) || String(Math.random()),
  searchable = true,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data available',
  loading = false,
  pageSize: pageSizeProp,
  page,
  totalPages: externalTotalPages,
  totalItems: externalTotalItems,
  onPageChange,
}: DataTableProps<T>) {
  const { tablePageSize, setTablePageSize } = useSettingsStore();
  const pageSize = pageSizeProp ?? tablePageSize;
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const isServerSide = !!onPageChange;

  // Reset to page 1 when search changes (client-side only)
  useEffect(() => {
    if (!isServerSide) setCurrentPage(1);
  }, [searchQuery, isServerSide]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const filteredData = searchable
    ? (data || []).filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : (data || []);

  const sortedData = sortKey
    ? [...filteredData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        const modifier = sortOrder === 'asc' ? 1 : -1;

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * modifier;
        }

        return String(aVal).localeCompare(String(bVal)) * modifier;
      })
    : filteredData;

  // Pagination
  let displayData: T[];
  let activePage: number;
  let totalPages: number;
  let totalCount: number;

  if (isServerSide) {
    displayData = sortedData;
    activePage = page || 1;
    totalPages = externalTotalPages || 1;
    totalCount = externalTotalItems || sortedData.length;
  } else {
    totalCount = sortedData.length;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    activePage = Math.min(currentPage, totalPages);
    const start = (activePage - 1) * pageSize;
    displayData = sortedData.slice(start, start + pageSize);
  }

  const rangeStart = totalCount === 0 ? 0 : (activePage - 1) * pageSize + 1;
  const rangeEnd = isServerSide
    ? Math.min(activePage * pageSize, totalCount)
    : Math.min(activePage * pageSize, totalCount);

  const goToPage = (p: number) => {
    if (isServerSide) {
      onPageChange!(p);
    } else {
      setCurrentPage(p);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      {searchable && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                  } ${column.className || ''}`}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span className="text-gray-400">
                        {sortKey === column.key ? (
                          sortOrder === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        ) : (
                          <ChevronsUpDown className="w-4 h-4" />
                        )}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary-600"></div>
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : displayData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                  {searchQuery ? `No results found for "${searchQuery}"` : emptyMessage}
                </td>
              </tr>
            ) : (
              displayData.map((item) => (
                <tr key={keyExtractor(item)} className="hover:bg-gray-50 transition-colors">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-6 py-4 text-sm text-gray-900 ${column.className || ''}`}
                    >
                      {column.render ? column.render(item) : String(item[column.key] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!loading && totalCount > 0 && (
        <div className="flex items-center justify-between text-sm text-gray-600 px-2">
          {totalPages > 1 ? (
            <div className="flex items-center space-x-1">
              <button
                onClick={() => goToPage(1)}
                disabled={activePage <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="First page"
              >
                <ChevronFirst className="w-4 h-4" />
              </button>
              <button
                onClick={() => goToPage(activePage - 1)}
                disabled={activePage <= 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-2">
                Page {activePage} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(activePage + 1)}
                disabled={activePage >= totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={activePage >= totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                title="Last page"
              >
                <ChevronLast className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span />
          )}
          <div className="flex items-center space-x-3">
            <span>
              Showing {rangeStart}–{rangeEnd} of {totalCount}
              {searchQuery && ` for "${searchQuery}"`}
            </span>
            {!pageSizeProp && (
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  setTablePageSize(newSize);
                  if (!isServerSide) setCurrentPage(1);
                }}
                className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white"
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt} / page</option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;

